import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  IconButton,
  Alert,
  useTheme,
} from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import CloseIcon from "@mui/icons-material/Close";
import { useMutation } from "@apollo/client";
import { SET_IDP_PRIORITY_ORDER } from "../../../../apis/settingsApis/integration-apis/integrationApis";
import { formatIntegrationLabel } from "../utils/integrationUtils";

/**
 * IdP Priority ordering dialog (Vanta-style).
 * Shows all active IdP connections as a draggable list (top = highest priority).
 * On save, sends the ordered rci ids; the backend assigns ranks by position.
 * Should only be opened when there are 2+ IdP connections.
 *
 * With multi-connect, a single integration can appear multiple times (e.g. two
 * Google Workspace tenants). Each row is a connection, disambiguated by
 * accountLabel when present.
 *
 * @param {Array} idpIntegrations - [{ rciId, label, accountLabel, logo_path, idpPriority }]
 */
const IdpPriorityDialog = ({
  open,
  onClose,
  idpIntegrations = [],
  onShowSnackbar,
  onSaved,
  hasHrmsIntegration = false,
}) => {
  const theme = useTheme();
  const [order, setOrder] = useState([]);
  const [dragIndex, setDragIndex] = useState(null);

  const [setIdpPriorityOrder, { loading: saving }] = useMutation(
    SET_IDP_PRIORITY_ORDER
  );

  // Seed the list from current priority (NULLs last, then stable by label).
  useEffect(() => {
    if (!open) return;
    const sorted = [...idpIntegrations].sort((a, b) => {
      const pa = a.idpPriority ?? Number.MAX_SAFE_INTEGER;
      const pb = b.idpPriority ?? Number.MAX_SAFE_INTEGER;
      if (pa !== pb) return pa - pb;
      return (a.label || "").localeCompare(b.label || "");
    });
    setOrder(sorted);
  }, [open, idpIntegrations]);

  const handleDragStart = (index) => setDragIndex(index);

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === index) return;
    setOrder((prev) => {
      const next = [...prev];
      const [moved] = next.splice(dragIndex, 1);
      next.splice(index, 0, moved);
      return next;
    });
    setDragIndex(index);
  };

  const handleDragEnd = () => setDragIndex(null);

  const handleSave = async () => {
    const rciIds = order
      .map((i) => i.rciId)
      .filter((id) => id !== null && id !== undefined);
    try {
      const result = await setIdpPriorityOrder({
        variables: { rciIds },
      });
      const payload = result?.data?.setIdpPriorityOrder;
      if (payload?.success) {
        onShowSnackbar?.({
          message: payload.message || "People source priority updated.",
          vertical: "top",
          horizontal: "right",
        });
        if (onSaved) await onSaved();
        onClose();
      } else {
        onShowSnackbar?.({
          message: payload?.message || "Failed to update people source priority.",
          vertical: "top",
          horizontal: "right",
        });
      }
    } catch (error) {
      console.error("Error saving IdP priority order:", error);
      onShowSnackbar?.({
        message: "An error occurred while saving priority order.",
        vertical: "top",
        horizontal: "right",
      });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
        }}
      >
        <Typography variant="h5" fontWeight={600}>
          People Source Priority
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 2 }}
        >
          When a person comes from more than one source, the one higher in this
          list is used as their source. Drag to reorder.
        </Typography>
        {hasHrmsIntegration && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>HRMS is always the primary source of employees.</strong> The
            priority order below is useful when you're using integrations other
            than HRMS as the source of your employee data.
          </Alert>
        )}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {order.map((item, index) => (
            <Box
              key={item.rciId}
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragEnd={handleDragEnd}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                px: 1.5,
                py: 1.25,
                borderRadius: "10px",
                border: `1px solid ${
                  dragIndex === index
                    ? theme.palette._blue?.main
                    : theme.customColors.border.main
                }`,
                backgroundColor:
                  dragIndex === index
                    ? theme.palette._blue?.light
                    : "#ffffff",
                boxShadow:
                  dragIndex === index
                    ? "0 4px 12px rgba(58,61,238,0.12)"
                    : "0 1px 2px rgba(0,0,0,0.04)",
                cursor: "grab",
                transition: "border-color 0.15s, box-shadow 0.15s",
                "&:hover": { borderColor: theme.palette._blue?.main },
                "&:active": { cursor: "grabbing" },
              }}
            >
              <DragIndicatorIcon
                sx={{ color: theme.customColors.icons.disabled, fontSize: 20 }}
              />
              {/* Logo tile */}
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "8px",
                  flexShrink: 0,
                  backgroundColor: theme.customColors.background.cardHeader,
                  border: `1px solid ${theme.customColors.border.main}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {item.logo_path ? (
                  <img
                    src={`${process.env.REACT_APP_POLICY_DOC_URL}/logos/${item.logo_path}`}
                    alt={formatIntegrationLabel(item.label)}
                    style={{ width: 20, height: 20, objectFit: "contain" }}
                  />
                ) : (
                  <Typography
                    sx={{
                      fontSize: "13px",
                      fontWeight: 600,
                      color: theme.customColors.text.secondary,
                    }}
                  >
                    {formatIntegrationLabel(item.label)?.charAt(0) || "?"}
                  </Typography>
                )}
              </Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  color: theme.customColors.text.strong,
                  flex: 1,
                }}
              >
                {formatIntegrationLabel(item.label)}
                {item.accountLabel ? (
                  <Typography
                    component="span"
                    sx={{
                      fontWeight: 400,
                      color: theme.customColors.text.secondary,
                      fontSize: "0.85em",
                      ml: 0.75,
                    }}
                  >
                    — {item.accountLabel}
                  </Typography>
                ) : null}
              </Typography>
              <Chip
                label={index === 0 ? "Primary" : `Priority ${index + 1}`}
                size="small"
                sx={{
                  height: 22,
                  fontWeight: 600,
                  fontSize: "0.7rem",
                  borderRadius: 1,
                  border: "none",
                  backgroundColor:
                    index === 0
                      ? theme.palette._blue?.light
                      : theme.customColors.background.light,
                  color:
                    index === 0
                      ? theme.palette._blue?.main
                      : theme.customColors.text.secondary,
                  "& .MuiChip-label": { px: 1 },
                }}
              />
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || order.length === 0}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IdpPriorityDialog;
